import { API_BASE_URL } from './httpClient';
/**
 * THERAPY TIMER - SERVICE GLOBAL CENTRALISÉ
 * 
 * Architecture:
 * - Un seul timer pour toute l'app (radio, playlist, therapeutic)
 * - Persistance localStorage (survit aux reloads)
 * - Reset automatique à minuit (changement de jour)
 * - API stricte: startActivity, pause, resume, stop
 * - Aucun timer local dans les composants React
 * 
 * Source of truth unique pour le temps d'écoute.
 */

const STORAGE_KEY = 'hearen_therapy_timer_v1';
const TICK_INTERVAL = 1000; // 1 seconde

// ===== BACKEND SYNC =====

/**
 * Sauvegarde une session thérapeutique complétée dans la base de données
 * @param {number} durationSeconds - Durée de la session en secondes
 * @param {Object} activity - Activité (type, label, category)
 * @param {Object} additionalParams - Paramètres additionnels pour enrichir la session
 */
async function saveSessionToBackend(durationSeconds, activity, additionalParams = {}) {
  console.log('[TherapyTimer] 📊 Input params:', {
    durationSeconds,
    activity,
    additionalParams
  });
  
  // GUARD: Minimum 10 seconds to save a session (prevents noise during iOS debugging)
  if (durationSeconds < 10) {
    console.log(`[TherapyTimer] ⏭️ SKIPPED: Duration ${durationSeconds}s < 10s minimum`);
    return;
  }
  
  try {
    // Récupérer le token d'authentification
    let authToken = null;
    try {
      authToken = localStorage.getItem('auth_token');
    } catch (e) {
      // Fallback pour Safari private mode
      try {
        authToken = sessionStorage.getItem('auth_token');
      } catch (e2) {
        console.error('[TherapyTimer] ❌ No storage available:', e2.message);
        return;
      }
    }
    
    if (!authToken) {
      console.error('[TherapyTimer] ❌ BLOCKED: No auth token available');
      return;
    }
    
    // Récupérer le user depuis le token (décodage JWT basique)
    let userId = null;
    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      userId = payload.sub;
    } catch (e) {
      console.error('[TherapyTimer] ❌ Failed to decode token:', e);
      return;
    }
    
    if (!userId) {
      console.error('[TherapyTimer] ❌ BLOCKED: No user ID in token');
      return;
    }
    
    // Préparer les données de session
    const sessionData = {
      user_id: userId,
      duration: durationSeconds,
      therapy_type: activity.type || 'unknown',
      parameters: {
        label: activity.label || 'Unknown',
        category: activity.category || 'general',
        ...additionalParams
      }
    };
    
    console.log('[TherapyTimer] 📤 Session data to send:', JSON.stringify(sessionData, null, 2));
    
    // Envoyer au backend
    const backendUrl = API_BASE_URL || '';
    const apiUrl = `${backendUrl}/api/therapy-sessions`;
    console.log('[TherapyTimer] 🌐 Step 4: Sending POST request to:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify(sessionData)
    });
    
    console.log('[TherapyTimer] 📥 Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[TherapyTimer] ❌ Backend error response:', errorText);
      // NON-BLOCKING: Log error but don't throw - allows audio to continue
      console.warn('[TherapyTimer] ⚠️ Session save failed but continuing (non-blocking)');
      return;
    }
    
    const result = await response.json();
    console.log('[TherapyTimer] 📊 Backend response:', result);
    
  } catch (error) {
    console.error('[TherapyTimer] ❌ === SAVE SESSION BACKEND FAILED ===');
    console.error('[TherapyTimer] ❌ Error details:', error);
    console.error('[TherapyTimer] ❌ Error message:', error.message);
    console.error('[TherapyTimer] ❌ Error stack:', error.stack);
    throw error; // Re-throw pour que l'appelant puisse gérer
  }
}

// ===== TYPES (pour documentation) =====
// type ActivityType = 'radio' | 'playlist' | 'therapeutic';
// 
// interface TherapyActivity {
//   type: ActivityType;
//   label?: string;    // ex. "Sleep Radio"
//   category?: string; // ex. "sleep"
// }
//
// interface TherapyTimerState {
//   isActive: boolean;
//   activity: TherapyActivity | null;
//   sessionStartAt: number | null; // timestamp ms
//   sessionElapsedMs: number;      // ms (session actuelle)
//   todayTotalMs: number;          // ms (total du jour)
//   lastTickAt: number | null;     // ms
//   currentDay: string;            // 'YYYY-MM-DD'
// }

// ===== STATE PRIVÉ (source of truth) =====
let state = {
  isActive: false,
  isPaused: false,     // NOUVEAU: pour distinguer pause vs stop
  activity: null,
  sessionStartAt: null,
  sessionElapsedMs: 0,
  todayTotalMs: 0,
  lastTickAt: null,
  currentDay: null,
  
  // Protection contre doublons
  sessionSavedToBackend: false  // Cette session a-t-elle été sauvegardée ?
};

// ===== LISTENERS =====
let listeners = new Set();

// ===== TICKER GLOBAL =====
let tickerIntervalId = null;

// ===== DEBOUNCE pour stop() =====
let stopDebounceTimer = null;

// ===== HELPERS =====

/**
 * Formate la date du jour en YYYY-MM-DD (timezone local)
 */
function formatToday() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Vérifie si localStorage est disponible (peut échouer en mode privé)
 */
function isLocalStorageAvailable() {
  try {
    const test = '__test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

const hasStorage = isLocalStorageAvailable();

if (!hasStorage) {
  console.warn('[TherapyTimer] localStorage unavailable (private mode?), running memory-only');
}

/**
 * Persiste l'état dans localStorage
 */
function persistState() {
  if (!hasStorage) return;
  
  try {
    const serializable = {
      isActive: state.isActive,
      isPaused: state.isPaused,
      activity: state.activity,
      sessionStartAt: state.sessionStartAt,
      sessionElapsedMs: state.sessionElapsedMs,
      todayTotalMs: state.todayTotalMs,
      lastTickAt: state.lastTickAt,
      currentDay: state.currentDay
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  } catch (error) {
    console.error('[TherapyTimer] Failed to persist:', error);
  }
}

/**
 * Restaure l'état depuis localStorage
 */
function restoreState() {
  if (!hasStorage) return false;
  
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return false;
    }
    
    const saved = JSON.parse(raw);
    const today = formatToday();
    
    // Vérifier changement de jour
    if (saved.currentDay !== today) {
      console.log(`[TherapyTimer] New day (${saved.currentDay} → ${today}), resetting`);
      return false;
    }
    
    // Restaurer (même jour)
    console.log(`[TherapyTimer] Restoring state from ${today}`);
    state.isActive = saved.isActive || false;
    state.isPaused = saved.isPaused || false;
    state.activity = saved.activity;
    state.sessionStartAt = saved.sessionStartAt;
    state.sessionElapsedMs = saved.sessionElapsedMs || 0;
    state.todayTotalMs = saved.todayTotalMs || 0;
    state.lastTickAt = Date.now(); // Reset pour repartir proprement
    state.currentDay = today;
    
    console.log(`[TherapyTimer] Restored: ${Math.floor(state.todayTotalMs / 60000)} min today`);
    return true;
  } catch (error) {
    console.error('[TherapyTimer] Failed to restore:', error);
    return false;
  }
}

/**
 * Calcule les valeurs dérivées pour l'affichage
 */
function getFormattedState() {
  const sessionElapsedSeconds = Math.floor(state.sessionElapsedMs / 1000);
  const todayTotalSeconds = Math.floor(state.todayTotalMs / 1000);
  
  const sessionMinutes = Math.floor(sessionElapsedSeconds / 60);
  const sessionSeconds = sessionElapsedSeconds % 60;
  
  const todayTotalMinutes = Math.floor(todayTotalSeconds / 60);
  
  const sessionFormatted = `${String(sessionMinutes).padStart(2, '0')}:${String(sessionSeconds).padStart(2, '0')}`;
  
  return {
    ...state,
    sessionElapsedSeconds,
    todayTotalSeconds,
    sessionMinutes,
    sessionSeconds,
    sessionFormatted,
    todayTotalMinutes
  };
}

/**
 * Notifie tous les listeners
 */
function notifyListeners() {
  const formattedState = getFormattedState();
  listeners.forEach(listener => {
    try {
      listener(formattedState);
    } catch (error) {
      console.error('[TherapyTimer] Listener error:', error);
    }
  });
}

/**
 * Tick global (appelé toutes les 1s)
 */
function tick() {
  if (!state.isActive || !state.lastTickAt) return;
  
  const now = Date.now();
  const delta = now - state.lastTickAt;
  
  // Incrémenter les compteurs
  state.sessionElapsedMs += delta;
  state.todayTotalMs += delta;
  state.lastTickAt = now;
  
  // Vérifier changement de jour
  const today = formatToday();
  if (today !== state.currentDay) {
    console.log(`[TherapyTimer] Day changed (${state.currentDay} → ${today}), RESET`);
    state.currentDay = today;
    state.todayTotalMs = 0;
    state.sessionElapsedMs = 0;
    state.activity = null;
    state.isActive = false;
    state.isPaused = false;
    state.sessionStartAt = null;
    state.lastTickAt = null;
  }
  
  // Persister & notifier
  persistState();
  notifyListeners();
}

/**
 * Démarre le ticker interne
 */
function startTicker() {
  if (tickerIntervalId !== null) return; // Déjà actif
  tickerIntervalId = setInterval(tick, TICK_INTERVAL);
}

/**
 * Arrête le ticker interne
 */
function stopTicker() {
  if (tickerIntervalId === null) return;
  clearInterval(tickerIntervalId);
  tickerIntervalId = null;
}

// ===== API PUBLIQUE =====

const therapyTimer = {
  /**
   * Initialise le timer (à appeler UNE SEULE FOIS au boot de l'app)
   */
  init() {
    
    const restored = restoreState();
    
    if (!restored) {
      // État neuf
      state.currentDay = formatToday();
      state.isActive = false;
      state.isPaused = false;
      state.activity = null;
      state.sessionStartAt = null;
      state.sessionElapsedMs = 0;
      state.todayTotalMs = 0;
      state.lastTickAt = null;
      persistState();
    }
    
    // Démarrer le ticker global (toujours actif)
    startTicker();
    
    // Notifier les listeners initiaux
    notifyListeners();
    
    console.log('[TherapyTimer] Initialized:', {
      isActive: state.isActive,
      todayTotalMin: Math.floor(state.todayTotalMs / 60000),
      currentDay: state.currentDay
    });
  },
  
  /**
   * Démarre ou continue une activité thérapeutique
   * 
   * IMPORTANT:
   * - Si actif : met à jour l'activité SANS reset (changement de radio)
   * - Si en pause : REPREND la session (PAS de reset)
   * - Si arrêté : NOUVELLE session (reset sessionElapsedMs)
   * 
   * @param {Object} activity - { type, label?, category? }
   */
  startActivity(activity) {
    console.log('[TherapyTimer] START ACTIVITY:', activity);
    console.log('[TherapyTimer] Current state: isActive=' + state.isActive + ', isPaused=' + state.isPaused + ', sessionElapsedMs=' + state.sessionElapsedMs + 'ms');
    
    const now = Date.now();
    
    // CAS 1: Déjà actif (radio joue) → juste mettre à jour l'activité
    if (state.isActive) {
      console.log('[TherapyTimer] Before switch: sessionElapsedMs=' + state.sessionElapsedMs + 'ms');
      
      state.activity = activity;
      persistState();
      notifyListeners();
      
      console.log('[TherapyTimer] After switch: sessionElapsedMs=' + state.sessionElapsedMs + 'ms (unchanged)');
      return;
    }
    
    // CAS 2: En pause → REPRENDRE la session existante
    if (state.isPaused && state.sessionElapsedMs > 0) {
      console.log('[TherapyTimer] Resuming paused session (sessionElapsedMs=' + state.sessionElapsedMs + 'ms)');
      
      state.isActive = true;
      state.isPaused = false;
      state.activity = activity; // Nouvelle activité (radio différente ou même)
      state.lastTickAt = now;
      
      persistState();
      notifyListeners();
      
      console.log('[TherapyTimer] Session resumed, timer continues from', Math.floor(state.sessionElapsedMs / 1000), 's');
      return;
    }
    
    // CAS 3: Complètement arrêté → NOUVELLE session
    
    // Réinitialiser le flag de sauvegarde pour la nouvelle session
    state.sessionSavedToBackend = false;
    
    state.isActive = true;
    state.isPaused = false;
    state.activity = activity;
    state.sessionStartAt = now;
    state.sessionElapsedMs = 0;
    state.lastTickAt = now;
    
    persistState();
    notifyListeners();
    
    console.log('[TherapyTimer] New session started (todayTotal:', Math.floor(state.todayTotalMs / 60000), 'min)');
  },
  
  /**
   * Met en pause (arrête le tick, conserve les durées)
   */
  pause() {
    console.log('[TherapyTimer] PAUSE (session frozen at', Math.floor(state.sessionElapsedMs / 1000), 's)');
    
    state.isActive = false;
    state.isPaused = true; // Marquer comme "en pause"
    // Ne pas modifier sessionElapsedMs ni todayTotalMs
    
    persistState();
    notifyListeners();
  },
  
  /**
   * Reprend (redémarre le tick)
   */
  resume() {
    console.log('[TherapyTimer] RESUME from', Math.floor(state.sessionElapsedMs / 1000), 's');
    
    state.isActive = true;
    state.isPaused = false;
    state.lastTickAt = Date.now();
    
    persistState();
    notifyListeners();
  },
  
  /**
   * Arrête complètement la session (ne reset PAS todayTotalMs)
   * Sauvegarde automatiquement la session dans la base de données
   * 
   * GARANTIE : Le timer s'arrête TOUJOURS, même si la sauvegarde échoue
   * 
   * @param {Object} options - Options de sauvegarde
   * @param {Object} options.additionalParams - Paramètres additionnels pour la session (playlist, coach, etc.)
   * @param {boolean} options.skipSave - Si true, ne sauvegarde pas au backend (default: false)
   */
  async stop(options = {}) {
    
    // PROTECTION 0 : Debounce - Si appelé trop rapidement, ignorer
    if (stopDebounceTimer) {
      return;
    }
    
    // Activer le debounce pour 300ms (réduit de 500ms)
    stopDebounceTimer = setTimeout(() => {
      stopDebounceTimer = null;
    }, 300);
    
    // Si déjà complètement arrêté (pas actif, pas en pause), ne rien faire
    if (!state.isActive && !state.isPaused) {
      return;
    }
    
    // Capturer les données AVANT tout reset (pour sauvegarde)
    const durationSeconds = Math.floor(state.sessionElapsedMs / 1000);
    const activity = state.activity;
    const wasActive = state.isActive;
    const wasPaused = state.isPaused;
    const hadSession = !!(state.sessionStartAt && activity);
    
    console.log('[TherapyTimer] 📊 Session info:', {
      duration: durationSeconds + 's',
      wasActive,
      wasPaused,
      hadSession,
      activity: activity?.type,
      sessionElapsedMs: state.sessionElapsedMs,
      todayTotalMs: state.todayTotalMs
    });
    
    // ============================================
    // ÉTAPE 1 : ARRÊTER LE TIMER (TOUJOURS)
    // ============================================
    
    state.isActive = false;
    state.isPaused = false;
    state.activity = null;
    state.sessionStartAt = null;
    state.sessionElapsedMs = 0;
    state.lastTickAt = null;
    state.sessionSavedToBackend = false; // Reset pour permettre une nouvelle session
    
    persistState();
    notifyListeners();
    
    
    // ============================================
    // ÉTAPE 2 : SAUVEGARDER SI CONDITIONS REMPLIES
    // ============================================
    
    // Vérifier si on doit sauvegarder
    // CORRECTION: Sauvegarder si actif OU en pause (les deux sont des sessions valides)
    const shouldSave = (
      hadSession &&
      (wasActive || wasPaused) &&  // Accepter actif OU pause
      !options.skipSave &&
      durationSeconds >= 1  // Seuil à 1s pour inclure les sessions très courtes
    );
    
    if (!shouldSave) {
      return;
    }
    
    // Sauvegarder la session
    
    try {
      await saveSessionToBackend(durationSeconds, activity, options.additionalParams);
    } catch (error) {
      console.error('[TherapyTimer] ❌ Failed to save session:', error);
      // Le timer est quand même arrêté, juste la sauvegarde a échoué
    }
  },
  
  /**
   * Récupère l'état actuel avec valeurs formatées
   */
  getState() {
    return getFormattedState();
  },
  
  /**
   * S'abonne aux changements d'état
   * 
   * @param {Function} listener - Callback appelée à chaque changement
   * @returns {Function} Fonction de cleanup
   */
  subscribe(listener) {
    listeners.add(listener);
    console.log('[TherapyTimer] Listener added, total:', listeners.size);
    
    // Notifier immédiatement avec l'état actuel formaté
    listener(getFormattedState());
    
    // Retourner fonction de cleanup
    return () => {
      listeners.delete(listener);
      console.log('[TherapyTimer] Listener removed, remaining:', listeners.size);
    };
  },
  
  /**
   * Se désabonne manuellement
   */
  unsubscribe(listener) {
    listeners.delete(listener);
  },
  
  /**
   * TEST FUNCTION: Force save current session immediately
   * FOR DEBUGGING ONLY
   */
  async forceSaveTest() {
    console.log('[TherapyTimer] Current state:', {
      isActive: state.isActive,
      isPaused: state.isPaused,
      sessionElapsedMs: state.sessionElapsedMs,
      activity: state.activity
    });
    
    const durationSeconds = Math.floor(state.sessionElapsedMs / 1000);
    const activity = state.activity || { type: 'therapeutic', label: 'Test', category: 'test' };
    
    console.log('[TherapyTimer] Forcing save with:', { durationSeconds, activity });
    
    try {
      await saveSessionToBackend(durationSeconds, activity, {});
    } catch (error) {
      console.error('[TherapyTimer] ❌ Force save failed:', error);
    }
  },
  
  /**
   * Reset complet du timer pour le jour actuel
   * Utile pour les tests ou pour corriger des données corrompues
   * ⚠️ À utiliser avec précaution : efface toutes les données du jour
   */
  resetToday() {
    
    // Arrêter toute session active
    state.isActive = false;
    state.isPaused = false;
    state.activity = null;
    state.sessionStartAt = null;
    state.sessionElapsedMs = 0;
    state.todayTotalMs = 0;
    state.lastTickAt = null;
    state.currentDay = formatToday();
    state.sessionSavedToBackend = false;
    
    // Vider le localStorage
    if (hasStorage) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('[TherapyTimer] ❌ Failed to clear localStorage:', error);
      }
    }
    
    // Persister le nouvel état propre
    persistState();
    
    // Notifier les listeners
    notifyListeners();
    
  }
};

// Auto-init au chargement du module
therapyTimer.init();

export default therapyTimer;

